import {
  CheckCircleOutlined,
  CloseOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Typography,
} from "antd";
import { useEffect, useState } from "react";

import { QCStatus } from "../../../enum/common";
import { ICreateQCResult } from "../../../models/Qc";
import { useCreateQcResult } from "../../../query/QcQuery";
import { notify } from "../../../utils/toast";

const { TextArea } = Input;
const { Title, Text } = Typography;
interface ISelectedPartN {
  id: number;
  requestId: number;
  qcStatus: QCStatus;
  partNumber: string;
  projectCode: string;
  project: string;
  description: string;
  manufacturer: string;
  actualQuantity: number;
  unit: string;
  location: {
    id: number;
    code: string;
    warehouse: {
      warehouseCode: string;
    };
  };
  supplierType: string;
  purchaser: string;
}
interface ModalQCEvaluationProps {
  open: boolean;
  record?: ISelectedPartN;
  onSuccess: () => void;
  onCancel: () => void;
}

const ModalQCEvaluation = ({
  open,
  record,
  onSuccess,
  onCancel,
}: ModalQCEvaluationProps) => {
  const [qtyNG, setQtyNG] = useState<number | null>(null);
  const createQcResultMutation = useCreateQcResult();
  const [form] = Form.useForm();

  useEffect(() => {
    if (open && record) {
      form.setFieldsValue({
        samplingSize: record.actualQuantity || 0,
      });
    }
  }, [open, record, form]);

  const handleQtyNGChange = (value: number | null) => {
    setQtyNG(value);
    const samplingSize = form.getFieldValue("samplingSize") || 0;

    if (samplingSize > 0 && value !== null) {
      const rate = Math.round((value / samplingSize) * 100 * 100) / 100;
      form.setFieldsValue({
        ngRate: rate,
        follow: rate > 0 ? QCStatus.REJECT : QCStatus.PASSED,
        typeError: rate === 0 && null,
        result: rate === 0 ? QCStatus.PASSED : QCStatus.REJECT,
      });
    } else {
      form.setFieldsValue({
        ngRate: 0,
      });
    }
  };

  const handleSamplingSizeChange = (value: number | null) => {
    const qtyNG = form.getFieldValue("qtyNG") || 0;
    if (value && value > 0) {
      const rate = Math.round((qtyNG / value) * 100 * 100) / 100;
      form.setFieldsValue({
        ngRate: rate,
        follow: rate > 0 ? QCStatus.REJECT : QCStatus.PASSED,
        typeError: rate === 0 && null,
        result: rate === 0 ? QCStatus.PASSED : QCStatus.REJECT,
      });
    } else {
      form.setFieldsValue({
        ngRate: 0,
      });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      if (!record?.requestId) {
        notify.error("Lỗi", "Vui lòng chọn yêu cầu kiểm tra");
        return;
      }
      const dto: ICreateQCResult = {
        requestId: record.requestId,
        partNumberId: record.id,
        supplierName: values.supplierName || "",
        materialsType: values.materialsType || "",
        customerName: values.customerName || "",
        samplingSize: values.samplingSize || 0,
        qtyNG: values.qtyNG || 0,
        ngRate: values.ngRate || 0,
        dateCode: values.dateCode || "",
        packing: values.packing || "",
        origin: values.origin || "",
        msl: values.msl || "",
        typeError: values.typeError || "",
        result: values.result || "",
        detailDescription: values.detailDescription || "",
        action: values.action || "",
        defect: values.defect || "",
        follow: values.follow || "",
        note: values.note || "",
      };
      await createQcResultMutation.mutateAsync(dto);
      notify.success("Đánh giá QC thành công");
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      if (error?.errorFields) return;
      notify.error(
        "Đánh giá QC thất bại",
        error?.message || "Vui lòng thử lại",
      );
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      open={open}
      title={
        <div className="flex items-center gap-2">
          <CheckCircleOutlined className="text-blue-500" />
          <Title level={4} className="!mb-0 text-gray-800">
            Đánh giá QC
          </Title>
        </div>
      }
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={createQcResultMutation.isPending}
      okText="Lưu đánh giá"
      cancelText="Hủy"
      okButtonProps={{
        icon: <SaveOutlined />,
        size: "large",
        loading: createQcResultMutation.isPending,
      }}
      cancelButtonProps={{
        icon: <CloseOutlined />,
        size: "large",
        disabled: createQcResultMutation.isPending,
      }}
      width={1200}
    >
      <div className="py-4">
        {record && (
          <Card size="small" className="mb-4 ">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircleOutlined className="text-blue-600" />
              <Text strong className="text-blue-800">
                Thông tin Part Number
              </Text>
            </div>
            <div className="grid grid-cols-3 gap-2 text-sm">
              <Text>
                <span className="font-medium">Part Number:</span>{" "}
                {record.partNumber}
              </Text>
              <Text>
                <span className="font-medium">Dự án:</span> {record.project}
              </Text>
              <Text>
                <span className="font-medium">Nhà sản xuất:</span>{" "}
                {record.manufacturer}
              </Text>
              <Text>
                <span className="font-medium">Số lượng:</span>{" "}
                {record.actualQuantity}
              </Text>
              <Text>
                <span className="font-medium">Vị trí:</span>{" "}
                {record.location?.code}
              </Text>
              <Text>
                <span className="font-medium">Type: </span>{" "}
                {record.supplierType}
              </Text>
              <Text>
                <span className="font-medium">Purchaser: </span>{" "}
                {record.purchaser}
              </Text>
            </div>
          </Card>
        )}

        <Form form={form} layout="vertical" size="middle">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Form.Item
              label="Tên nhà cung cấp"
              name="supplierName"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <Input placeholder="Nhập tên nhà cung cấp" />
            </Form.Item>

            <Form.Item
              label="Loại vật liệu"
              name="materialsType"
              rules={[{ required: true, message: "Vui lòng chọn" }]}
            >
              <Select
                placeholder="Chọn loại vật liệu"
                options={[
                  { label: "Linh kiện ĐTTC", value: "STANDARD_PART" },
                  { label: "Linh kiện cơ khí", value: "MECHANICS_PART" },
                  { label: "PCB-A", value: "PCB_A" },
                  { label: "Linh kiện Điện", value: "ELECTRONICS_PART" },
                  { label: "Máy móc thiết bị", value: "MACHINES" },
                  { label: "Loại khác", value: "OTHER" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Tên khách hàng"
              name="customerName"
              rules={[{ required: true, message: "Vui lòng chọn" }]}
            >
              <Select
                placeholder="Chọn khách hàng"
                allowClear
                options={[
                  { label: "Vinfast", value: "VINFAST" },
                  { label: "Viettel - VTX", value: "VTX" },
                  { label: "Viettel - VHT", value: "VHT" },
                  { label: "Viettel - VMC", value: "VMC" },
                  { label: "Nội bộ", value: "INTERNAL" },
                  { label: "Khác", value: "OTHER" },
                ]}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item
              label="Số lượng mẫu"
              name="samplingSize"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <InputNumber
                min={0}
                max={record?.actualQuantity}
                placeholder="Số lượng kiểm tra"
                style={{ width: "100%" }}
                onChange={handleSamplingSizeChange}
              />
            </Form.Item>

            <Form.Item
              label="Số lượng NG"
              name="qtyNG"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <InputNumber
                min={0}
                max={record?.actualQuantity}
                placeholder="Số lượng lỗi"
                style={{ width: "100%" }}
                onChange={handleQtyNGChange}
              />
            </Form.Item>

            <Form.Item
              label="Tỷ lệ NG (%)"
              name="ngRate"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <InputNumber
                min={0}
                max={100}
                placeholder="Tỷ lệ lỗi (tự động)"
                style={{ width: "100%" }}
              />
            </Form.Item>

            <Form.Item
              label="Date Code"
              name="dateCode"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <Input placeholder="Mã ngày sản xuất" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item
              label="Đóng gói"
              name="packing"
              rules={[{ required: true, message: "Vui lòng chọn" }]}
            >
              <Select
                placeholder="Chọn loại đóng gói"
                options={[
                  { label: "Bulk", value: "BULK" },
                  { label: "Reel (Cuộn)", value: "REEL" },
                  { label: "Tray (Khay)", value: "TRAY" },
                  { label: "Tube (Ống)", value: "TUBE" },
                  { label: "Bag (Túi)", value: "BAG" },
                ]}
              />
            </Form.Item>

            <Form.Item
              label="Xuất xứ"
              name="origin"
              rules={[{ required: true, message: "Vui lòng nhập" }]}
            >
              <Input placeholder="Xuất xứ" />
            </Form.Item>

            <Form.Item label="MSL" name="msl">
              <Input placeholder="Moisture Sensitivity Level" />
            </Form.Item>

            <Form.Item label="Kiểu lỗi" name="typeError">
              <Select
                disabled={qtyNG === 0}
                allowClear
                placeholder="Chọn kiểu lỗi"
                options={[
                  { label: "Lỗi do nhà cung cấp", value: "YES" },
                  { label: "Lỗi nội bộ", value: "NO" },
                ]}
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Form.Item
              label="Kết quả"
              name="result"
              rules={[{ required: true, message: "Vui lòng chọn" }]}
            >
              <Select
                placeholder="Chọn kết quả QC"
                options={[
                  { label: "OK", value: QCStatus.PASSED },
                  { label: "NG", value: QCStatus.REJECT },
                  { label: "PATIAL", value: QCStatus.PARTIAL_REJECT },
                ]}
              />
            </Form.Item>

            <Form.Item label="Loại lỗi" name="defect">
              <Select
                disabled={qtyNG === 0}
                placeholder="Chọn loại lỗi"
                options={[
                  { label: "Chức năng", value: "FUNCTION" },
                  { label: "Kích thước", value: "DIMENSION" },
                  { label: "Ngoại quan", value: "APPEARANCE" },
                  { label: "Đóng gói", value: "PACKING" },
                  { label: "Hợp đồng", value: "CONTRACT" },
                ]}
              />
            </Form.Item>

            <Form.Item label="Hành động" name="action">
              <Input placeholder="Hành động xử lý" disabled={qtyNG === 0} />
            </Form.Item>
          </div>

          <Form.Item label="Mô tả chi tiết" name="detailDescription">
            <TextArea
              placeholder="Mô tả chi tiết về kết quả QC..."
              rows={3}
              showCount
              maxLength={500}
            />
          </Form.Item>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Form.Item label="Theo dõi" name="follow">
              <Select placeholder="Theo dõi" allowClear>
                <Select.Option value={QCStatus.REJECT}>NG</Select.Option>
                <Select.Option value={QCStatus.PASSED}>OK</Select.Option>
              </Select>
            </Form.Item>

            <Form.Item label="Ghi chú" name="note">
              <TextArea
                placeholder="Ghi chú thêm (không bắt buộc)..."
                rows={2}
                showCount
                maxLength={300}
              />
            </Form.Item>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default ModalQCEvaluation;
